import { z } from 'zod';
import { GEO_ACCURACY_MAX_METRES } from '../constants/dutyEvent.constant';
import {
  LOCATION_SHARE_OUTCOMES,
  LOCATION_SHARE_STATES,
  OFFICER_LOCATION_MAX_ENTRIES,
  OFFICER_TRAIL_MAX_ENTRIES,
} from '../constants/officerTrail.constant';
import { objectIdSchema } from './common.schema';
import { orgUnitSummarySchema } from './orgUnit.schema';
import { rankSummarySchema } from './rank.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  AN OFFICER'S TRAIL — THE WIRE FORMAT. [ANALYTICS §6]
 *
 *  Two audiences, and they never meet:
 *
 *   · the OFFICER APP sends its own location (`POST /app/me/location`), and Duty Mitra asks first
 *     whether it should (`GET /app/me/location/status`). It is never shown a trail — not its
 *     own, not anybody's — and nothing it receives echoes a coordinate back;
 *   · the USAGE ANALYTICS page reads one officer's trail (`GET /analytics/officers/:id/trail`),
 *     SUPER_ADMIN only, and every read writes `OFFICER_TRAIL_VIEWED`.
 *
 *  ⚠️ NO OTHER SCHEMA IN THIS PACKAGE CARRIES AN ADDRESS OR A LOCATION OF AN OFFICER, and none
 *  may start to. `officerTrailExposure.test.ts` scans every reachable response for a sentinel
 *  address and coordinate, so a DTO that grew one would be a red test rather than a leak.
 *  ⚠️ [amended LOCATION-INTEL] ONE NOW DOES, BY THE USER'S BRIEF: `locationIntel.schema.ts` carries each
 *  in-scope officer's NEWEST location — one point, never the list, no address — to holders of the
 *  `location_intel` flag. Its own test (`locationIntel.test.ts`) pins what it carries; the sentinel scan's
 *  seventeen responses are the ones that must still carry none, and they do.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ THE DEVICE'S FIX AND THE DEVICE'S CLOCK. `capturedAt` is the phone's and may be wrong — the
 * server stores it beside its OWN receipt time and keys everything it decides (the status route's
 * fifteen-minute advice, the retention purge) on the second. [P5c]'s two-clock rule, one collection over.
 *
 * The bounds are `dutyEventInputSchema`'s geo block's, so one handset fix is valid on both.
 */
export const shareLocationBodySchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracyMetres: z.number().min(0).max(GEO_ACCURACY_MAX_METRES).nullable(),
  capturedAt: z.iso.datetime(),
});

/**
 * Whether Duty Mitra should send NOW — a duty in progress, and nothing recorded in the last fifteen
 * minutes. [amended LOCATION-ANYTIME] Advice: the POST records every send whatever this says.
 */
export const locationShareStateSchema = z.object({
  state: z.enum(LOCATION_SHARE_STATES),
});

/**
 * ⚠️ AN OUTCOME AND NOTHING ELSE. The body is never echoed back: an app that wants to show "your
 * location was shared" already holds the fix it sent, and a response carrying coordinates is one
 * more place a trail can be cached, logged or intercepted.
 */
export const locationShareResultSchema = z.object({
  outcome: z.enum(LOCATION_SHARE_OUTCOMES),
});

export const officerTrailParamsSchema = z.object({
  officerId: objectIdSchema,
});

/**
 * ⚠️ `observedAt` IS WHEN THIS ADDRESS MOST RECENTLY **BECAME** CURRENT — never its last request.
 * The list is distinct and newest first: an officer who goes A → B → A holds `[A, B]`, and A's
 * instant is the second time it became current. Recording the last request instead would be a
 * write per request, which is what the one-write-per-change rule exists to avoid.
 */
export const officerTrailAddressSchema = z.object({
  ip: z.string(),
  observedAt: z.iso.datetime(),
});

/** ⚠️ Both clocks, labelled for what they are — `capturedAt` is the phone's, `recordedAt` the server's. */
export const officerTrailLocationSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  accuracyMetres: z.number().nullable(),
  capturedAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
});

export const officerTrailSchema = z.object({
  officer: z.object({
    id: objectIdSchema,
    name: z.string(),
    isActive: z.boolean(),
    rank: rankSummarySchema.nullable(),
    orgUnit: orgUnitSummarySchema.nullable(),
  }),
  addresses: z.array(officerTrailAddressSchema).max(OFFICER_TRAIL_MAX_ENTRIES),
  locations: z.array(officerTrailLocationSchema).max(OFFICER_LOCATION_MAX_ENTRIES),
  /** The purge window the page states, so the sentence cannot drift from the server's setting. */
  retentionDays: z.number().int().positive(),
});
